import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Divider, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Stack, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import { UserContext } from '../../../contexts/UserContext';

const EMPTY_FORM = { module: '', status: 'Active', expiry: '', maxUsers: 0, currentUsers: 0, gracePeriodDays: 0 };

export default function LicenseManagementPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('system-settings');
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchJson('/api/super-admin/licenses');
      setLicenses(Array.isArray(data?.licenses) ? data.licenses : []);
    } catch (e) {
      setError(e?.message || 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    if (!canManage) return;
    setSelectedLicense(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (license) => {
    if (!canManage) return;
    setSelectedLicense(license);
    setForm({
      module: license.module || '',
      status: license.status || 'Active',
      expiry: license.expiry || '',
      maxUsers: license.maxUsers || 0,
      currentUsers: license.currentUsers || 0,
      gracePeriodDays: license.gracePeriodDays || 0
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!canManage) return;
    try {
      let updated;
      if (selectedLicense) {
        updated = licenses.map(l => l.id === selectedLicense.id ? { ...l, ...form } : l);
      } else {
        const newId = licenses.length > 0 ? Math.max(...licenses.map(l => l.id || 0)) + 1 : 1;
        updated = [...licenses, { id: newId, ...form }];
      }
      await fetchJson('/api/super-admin/licenses', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenses: updated })
      });
      setLicenses(updated);
      setSnack('License saved');
      setDialogOpen(false);
    } catch (e) {
      setError(e?.message || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!canManage) return;
    try {
      const updated = licenses.filter(l => l.id !== id);
      await fetchJson('/api/super-admin/licenses', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenses: updated })
      });
      setLicenses(updated);
      setSnack('License removed');
    } catch (e) {
      setError(e?.message || 'Failed to delete');
    }
  };

  const columns = [
    { field: 'module', headerName: 'Module', flex: 1 },
    { field: 'status', headerName: 'Status', width: 120,
      renderCell: (params) => <Chip label={params.value} color={params.value === 'Active' ? 'success' : params.value === 'Trial' ? 'warning' : 'default'} size="small" />
    },
    { field: 'expiry', headerName: 'Expiry', width: 130 },
    { field: 'maxUsers', headerName: 'Max Users', width: 110, type: 'number' },
    { field: 'currentUsers', headerName: 'Current', width: 100, type: 'number' },
    { field: 'gracePeriodDays', headerName: 'Grace (days)', width: 110, type: 'number' },
    { field: 'actions', headerName: 'Actions', width: 180, renderCell: (params) => (
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" onClick={() => openEdit(params.row)} disabled={!canManage}>Edit</Button>
        <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(params.row.id)} disabled={!canManage}>Delete</Button>
      </Stack>
    ) },
  ];

  return (
    <Box width="100%" my={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} mb={2}>
        <Typography variant="h5" fontWeight={700}>{t('licenseManagement.title') || 'License Management'}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!canManage}>Add License</Button>
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <div style={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={licenses}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10, 25]}
          disableSelectionOnClick
        />
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedLicense ? 'Edit License' : 'Add License'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Module Name" value={form.module} onChange={e => setForm(f => ({ ...f, module: e.target.value }))} fullWidth required />
            <TextField label="Status" select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} fullWidth
              SelectProps={{ native: true }}>
              <option value="Active">Active</option>
              <option value="Trial">Trial</option>
              <option value="Expired">Expired</option>
              <option value="Suspended">Suspended</option>
            </TextField>
            <TextField label="Expiry Date" type="date" value={form.expiry} onChange={e => setForm(f => ({ ...f, expiry: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="Max Users" type="number" value={form.maxUsers} onChange={e => setForm(f => ({ ...f, maxUsers: Number(e.target.value) }))} fullWidth />
              <TextField label="Current Users" type="number" value={form.currentUsers} onChange={e => setForm(f => ({ ...f, currentUsers: Number(e.target.value) }))} fullWidth />
            </Stack>
            <TextField label="Grace Period (days)" type="number" value={form.gracePeriodDays} onChange={e => setForm(f => ({ ...f, gracePeriodDays: Number(e.target.value) }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.module || !canManage}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
